import { useState } from 'react';
import { ClipboardList, Flame, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';

const BenefitsSection = () => {
  const [activeTab, setActiveTab] = useState('inventory');

  const businessPillars = {
    inventory: {
      title: "Stop Losing Money in the Stock Room",
      subtitle: "Smart Stock & Ingredient Tracking",
      description: "Ever wonder where your food margins are disappearing? SOKO tracks your raw ingredients from the moment the delivery truck arrives down to the single scoop or serving. If a branch is wasting milk, sugar, or expensive ingredients, you will know before the shift ends.",
      points: [
        "Real-time alerts when stock levels run low",
        "Track recipe costs against actual daily sales",
        "Spot variance and hidden waste across locations"
      ],
      icon: <ClipboardList className="w-6 h-6 text-blue-400" />
    },
    kitchen: {
      title: "Keep Your Kitchen Moving on Busy Friday Nights",
      subtitle: "Smooth Order & Kitchen Flow",
      description: "Don't let lost paper tickets slow down your cooks. SOKO syncs your front-of-house servers instantly with screen displays in the kitchen. Orders are color-coded by wait time, ensuring modifications are never missed and food gets to the table hot.",
      points: [
        "Eliminate paper tickets with clean kitchen screens",
        "Instant alerts for modifications or allergies",
        "Track exactly how long a table waits for food"
      ],
      icon: <Flame className="w-6 h-6 text-cyan-400" />
    },
    management: {
      title: "See Daily Profits Without Calling Every Manager",
      subtitle: "Multi-Location Live Control",
      description: "You shouldn't have to wait for end-of-month spreadsheets to know how your business is doing. SOKO aggregates total sales, labor costs, and net margins across all your branches into a single view on your phone.",
      points: [
        "Compare top-selling items between branches",
        "Check labor costs against live sales hourly",
        "Access everything securely from home or on the road"
      ],
      icon: <TrendingUp className="w-6 h-6 text-indigo-400" />
    }
  };

  return (
    <section className="py-24 bg-zinc-950 text-white relative border-t border-zinc-900" id="solutions">
      
      {/* Soft Ambient Light Background */}
      <div className="absolute top-[30%] right-[-10%] w-[50%] h-[40%] bg-blue-500/5 rounded-full blur-[140px] pointer-events-none"></div>

      <div className="w-full px-6 sm:px-12 lg:px-16 mx-auto">
        
        {/* --- SECTION HEADER --- */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 font-mono">
            // Built For Daily Operations
          </span>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tight">
            Designed to fix your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-400 to-zinc-600">
              toughest restaurant headaches
            </span>
          </h2>
        </div>

        {/* --- INTERACTIVE BUSINESS CONTROLLERS --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Side: Problem Nav Toggles */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            {Object.keys(businessPillars).map((key) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`w-full text-left p-5 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                  activeTab === key
                  ? 'bg-zinc-900 border-blue-500/50 shadow-lg shadow-blue-950/20 text-white'
                  : 'bg-zinc-900/40 border-zinc-800/60 text-zinc-400 hover:bg-zinc-900 hover:border-zinc-700'
                }`}
              >
                <div className={`p-2 rounded-lg transition-colors ${
                  activeTab === key ? 'bg-blue-500/10' : 'bg-zinc-950'
                }`}>
                  {businessPillars[key].icon}
                </div>
                <div>
                  <p className="text-[10px] font-mono font-bold uppercase tracking-wider opacity-60">
                    {businessPillars[key].subtitle}
                  </p>
                  <p className="text-base font-bold mt-0.5">
                    {key === 'inventory' ? 'Stock & Food Waste' : key === 'kitchen' ? 'Kitchen Coordination' : 'Multi-Branch Sales'}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Right Side: Dynamic High-Contrast Value Display Box */}
          <div className="lg:col-span-8 bg-zinc-900 border border-zinc-800 rounded-2xl p-8 lg:p-12 relative min-h-[420px] flex flex-col justify-between">
            
            <div className="space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider block font-mono">
                  {businessPillars[activeTab].subtitle}
                </span>
                <h3 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight">
                  {businessPillars[activeTab].title}
                </h3>
              </div>

              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed font-sans max-w-2xl">
                {businessPillars[activeTab].description}
              </p>

              {/* Bullet Proof Points */}
              <div className="grid grid-cols-1 sm:grid-cols-1 gap-3 pt-4">
                {businessPillars[activeTab].points.map((point, index) => (
                  <div key={index} className="flex items-start gap-3 text-zinc-300 text-sm font-medium">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Subtle Fake Live Operational Status Indicator */}
            <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500 font-mono">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-zinc-600" />
                <span>SOKO Module Operational</span>
              </div>
              <span>Getsoko.app</span>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default BenefitsSection;